import React from 'react';
import { sectionTitles, defaultPlaceholderData } from '../common/resumeConstants';
import { 
  ContactInfo,
  ResumeSection,
  EducationSection,
  CertificateSection,
  SkillSection,
  SummarySection,
  ExperienceSection,
  PaperSection,
  CoreCompetenciesSection,
  getPlaceholderClass
} from '../common/ResumeComponents';
import { 
  checkHasRealData, 
  getCurrentData, 
  getPaperTypeText
} from '../common/resumeHelpers';

// Template 4 - 우아한 사이드바 스타일
const Template4 = ({ resumeData, updatingField, language = 'kr' }) => {
  const titles = sectionTitles[language] || sectionTitles.kr;
  const hasRealData = checkHasRealData(resumeData);
  const currentData = getCurrentData(resumeData, defaultPlaceholderData, hasRealData);

  // 이름을 대문자로 변환하고 글자 사이에 공백 추가
  const formatDisplayName = (name) => {
    return name.toUpperCase().split('').join(' ');
  };

  return (
    <div className={`template-4 resume-container bg-white ${language === 'en' ? 'resume-english' : ''}`}>
      {/* Header */}
      <div className="text-center p-6 border-b border-gray-200">
        <h1 className={`text-4xl font-bold text-gray-800 mb-2 uppercase name-display ${getPlaceholderClass(hasRealData.name, '', 'italic text-gray-300')}`}>
          {formatDisplayName(currentData.name)}
        </h1>
        <p className={getPlaceholderClass(hasRealData.title, 'text-lg text-gray-600', 'text-gray-300 italic text-lg')}>
          {currentData.title}
        </p>
      </div>

      {/* Content Container */}
      <div className="flex" style={{ minHeight: '900px' }}>
        {/* Left Sidebar */}
        <div className="w-1/3 bg-purple-50 p-6">
          {/* Contact */}
          <div className="mb-10">
            <h2 className="text-xl font-bold text-purple-900 mb-4 uppercase">{titles.contact}</h2>
            <ContactInfo
              phone={currentData.phone}
              email={currentData.email}
              address={currentData.address}
              socialLinks={currentData.socialLinks}
              hasRealData={hasRealData}
              direction="vertical"
              className="space-y-3"
            />
          </div>

          {/* Education */}
          {currentData.education.length > 0 && (
            <div className="mb-10">
              <h2 className="text-xl font-bold text-purple-900 mb-4 uppercase">{titles.education}</h2>
              <EducationSection
                education={currentData.education}
                hasRealData={hasRealData}
                itemClassName="mb-3 pb-3 border-b border-dotted border-purple-200 last:border-b-0 last:pb-0 last:mb-0"
                datePosition="inline"
                showMajor={true}
                degreeClassName="text-base font-semibold text-gray-800"
                schoolClassName="text-sm text-gray-700"
                dateClassName="text-xs sm:text-sm text-gray-600"
                majorClassName="text-xs text-gray-500"
              />
            </div>
          )}

          {/* Certificates */}
          {currentData.certificates.length > 0 && (
            <div className="mb-10">
              <h2 className="text-xl font-bold text-purple-900 mb-4 uppercase">{titles.certificates}</h2>
              <CertificateSection
                certificates={currentData.certificates}
                hasRealData={hasRealData}
                itemClassName="mb-2 pb-2 border-b border-dotted border-purple-200 last:border-b-0 last:pb-0 last:mb-0"
                dateFormat="inline"
                nameClassName="text-sm font-semibold text-gray-800"
                issuerClassName="text-xs text-gray-600"
              />
            </div>
          )}

          {/* Skills */}
          {currentData.skills.length > 0 && (
            <div className="mb-10">
              <h2 className="text-xl font-bold text-purple-900 mb-4 uppercase">{titles.skills}</h2>
              <ul className="space-y-2">
                {currentData.skills.map((skill, index) => (
                  <li key={index} className={getPlaceholderClass(hasRealData.skills, 'text-sm text-gray-700')}>
                    {skill}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Right Content */}
        <div className="w-2/3 p-6 space-y-6">
          {/* Summary */}
          {(currentData.summary || currentData.keyStrengths.length > 0) && (
            <section>
              <h2 className="text-xl font-bold text-gray-800 mb-4 uppercase">{titles.introduction}</h2>
              {currentData.summary && (
                <SummarySection
                  summary={currentData.summary}
                  hasRealData={hasRealData}
                  className="mb-4"
                  textClassName="text-sm text-gray-700"
                />
              )}
              {currentData.keyStrengths.length > 0 && (
                <div className="mt-4">
                  <h3 className="text-base font-semibold text-gray-700 mb-3">{titles.coreCompetencies}</h3>
                  <CoreCompetenciesSection
                    keyStrengths={currentData.keyStrengths}
                    hasRealData={hasRealData}
                    bulletStyle="•"
                    bulletClassName="text-purple-400"
                    textClassName="text-sm text-gray-700"
                  />
                </div>
              )}
            </section>
          )}

          {/* Experience */}
          {currentData.experiences.length > 0 && (
            <section>
              <h2 className="text-xl font-bold text-gray-800 mb-4 uppercase">{titles.experience}</h2>
              {currentData.experiences.map((exp, index) => (
                <div key={index} className="mb-6 last:mb-0">
                  <h3 className={getPlaceholderClass(hasRealData.experiences, 'text-lg font-semibold text-gray-800')}>
                    {exp.position}
                  </h3>
                  <p className={getPlaceholderClass(hasRealData.experiences, 'text-sm font-medium text-gray-700 mb-1')}>
                    {exp.company}
                  </p>
                  <p className={getPlaceholderClass(hasRealData.experiences, 'text-sm text-gray-600 mb-3')}>
                    {exp.startDate} - {exp.endDate}
                  </p>
                  
                  {exp.overallDescription && (
                    <ul className="space-y-2 mb-3">
                      {exp.overallDescription.split('\n').filter(line => line.trim()).map((line, i) => (
                        <li key={i} className={`${getPlaceholderClass(hasRealData.experiences, 'text-sm text-gray-700')} flex items-start gap-2`}>
                          <span className="text-purple-400">•</span>
                          <span>{line.replace(/^[-•]\s*/, '')}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  
                  {exp.projects && exp.projects.length > 0 && (
                    <div className="mt-4 space-y-3">
                      {exp.projects.map((project, projIndex) => (
                        <div key={projIndex}>
                          <h4 className={getPlaceholderClass(hasRealData.experiences, 'text-sm font-semibold text-gray-700 mb-1')}>
                            {project.projectName}
                          </h4>
                          {project.projectDescription && (
                            <ul className="space-y-1">
                              {project.projectDescription.split('\n').filter(line => line.trim()).map((line, i) => (
                                <li key={i} className={`${getPlaceholderClass(hasRealData.experiences, 'text-sm text-gray-700')} flex items-start gap-2`}>
                                  <span className="text-purple-400">•</span>
                                  <span>{line.replace(/^[-•]\s*/, '')}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </section>
          )}

          {/* Papers */}
          {hasRealData.papers && (
            <section>
              <h2 className="text-xl font-bold text-gray-800 mb-4 uppercase">{titles.papers}</h2>
              <PaperSection
                papers={currentData.papers}
                hasRealData={hasRealData}
                getPaperTypeText={getPaperTypeText}
                titleClassName="text-base font-semibold text-gray-800"
                authorsClassName="text-sm text-gray-600"
                journalClassName="text-sm text-gray-500"
                typeClassName="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded"
                showType={true}
              />
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default Template4;
